import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Building } from '../../../../resources/building/building.interface';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { BuildingService } from '../../../../resources/building/building.service';
import { Subject } from 'rxjs';
import { AuthService } from '../../../../auth/auth.service';

/**
 * Building info block.
 *
 * Receives its initial value from the parent component, after which it is matched to
 * a Building from the Buildings observable.
 */
@Component({
  selector: 'sg2-building-info-ratio',
  templateUrl: './building-info-ratio.component.html',
  styleUrls: ['./building-info-ratio.component.less']
})
export class BuildingInfoRatioComponent implements OnDestroy, OnInit {

   /** Constants. */
   private readonly DEFAULT_RATIO = 1;
   private readonly DEFAULT_RATIO_AS = 'ratio';
   private readonly DEFAULT_RATIO_BY = 'building';

  /** Active building. */
  @Input() building: Building;

  /** Dispatch action for legacy support. */
  @Output() action = new EventEmitter<{
    ratio: Building['ratio'];
    fno: number;
    bvo: number;
  }>();

  /** Destroy hook. */
  private destroy$ = new Subject<any>();

  /** Form. */
  public form: FormGroup;


  /** Load the formbuilder. */
  constructor(
    private authService: AuthService,
    private buildingService: BuildingService,
    private formBuilder: FormBuilder
  ) { }

  /**
   * Initialize form with the Input building. Then, subscribe to Building.
   */
  ngOnInit() {
    this._patchBuilding();
    this._initForm();
    this._trackBVO();
    this._trackRatio();
    this._trackBuilding();
  }

  /** Unsubscribe on destroy. */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Get the building BUI value.
   *
   * Building BUI is a sum of the active cluster's BUI values.
   *
   * @returns Building BUI value.
   */
  private _getBuildingBui(): number {
    let bui = 0;
    for (const cluster of this.building.clusters) {
      bui += cluster.areas
        .filter(a => a.active && a.functionality === 5)
        .reduce((sum, a) => sum + (a.surface * a.quantity), 0);
    }
    return bui;
  }

  /**
   * Initialize new FNO/BVO form.
   *
   * The form should only update *on blur*, to prevent unnecessary API calls.
   */
  private _initForm(): void {
    const fno = this.buildingService.getFno(this.building);
    this.form = this.formBuilder.group({
      ratio: [ parseFloat(this.building.ratio.toFixed(2)), [ Validators.min(1) ]],
      fno: [{ value: parseFloat(fno.toFixed(2)), disabled: true }],
      bvo: null,
      bui: [{ value: this._getBuildingBui(), disabled: true }]
    }, {
      updateOn: 'blur'
    });

    // For performance reasons it's best not to call the floorAreaService twice,
    // so patch the BVO input field with local data, and prevent triggering the
    // subscription with { emitEvent: false }, which in turn prevents the API call
    // on a form field value change.
    const bvo = parseFloat((this.form.controls.fno.value * this.form.controls.ratio.value).toFixed(2));
    this.form.patchValue({
      bvo: [{ value: bvo }]
    }, {
      emitEvent: false
    });
  }

  /**
   * Determine whether to disable the form field based on current `ratioAs` and `ratioBy`.
   *
   * The variables are not used, they're just so you can call a function through ternary.
   */
  private _setDisabled(): void {
    const ratio = (this.building.ratioBy !== 'building' || this.building.ratioAs !== 'ratio' || !this.authService.canUpdate('building'))
      ? this.form.get('ratio').disable({ emitEvent: false })
      : this.form.get('ratio').enable({ emitEvent: false });
    const bvo = (this.building.ratioBy !== 'building' || this.building.ratioAs !== 'bvo' || !this.authService.canUpdate('building'))
      ? this.form.get('bvo').disable({ emitEvent: false })
      : this.form.get('bvo').enable({ emitEvent: false });
  }

  /**
   * Patch building.
   *
   * Make sure the building contains all the properties needed.
   */
  private _patchBuilding(): void {
    this.building = {
      ...this.building,
      ratio: this.building.ratio || this.DEFAULT_RATIO,
      ratioAs: this.building.ratioAs || this.DEFAULT_RATIO_AS,
      ratioBy: this.building.ratioBy || this.DEFAULT_RATIO_BY
    };
  }

  /**
   * Track building from the Building observable.
   *
   * Match the current building by id, so only the building instead of the whole
   * Buildings array is received.
   */
  private _trackBuilding(): void {
    this.buildingService.building$.pipe(
      takeUntil(this.destroy$),
      map(buildings => buildings.filter(building => building._id === this.building._id)[0])
    ).subscribe(building => {
      this.building = building;
      this._patchBuilding();
      this._updateForm();
    });
  }

  /**
   * Track the BVO form field.
   *
   * Update the Ratio form field on updating the BVO value.
   *
   * Pipeline:
   * - Only call API when value has changed.
   * - The BVO has a min value of the current FNO (so the Ratio cannot fall below 1).
   */
  private _trackBVO(): void {
    this.form.controls.bvo.valueChanges.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map(bvo => bvo < this.form.controls.fno.value ? this.form.controls.fno.value : bvo)
    ).subscribe(bvo => {
      const ratio = bvo / this.form.controls.fno.value;
      this.buildingService.setRatio(this.building, ratio);
      this.action.emit({ ratio: this.building.ratio, fno: this.form.controls.fno.value, bvo });
    });
  }

  /**
   * Track the Ratio form field.
   *
   * Update the BVO form field on updating the Ratio value.
   *
   * Pipeline:
   * - Only call API when value has changed.
   * - The ratio has a min value of 1.
   *
   * Features an event emitter for legacy support.
   */
  private _trackRatio(): void {
    this.form.controls.ratio.valueChanges.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map(ratio => ratio > 1 ? ratio : 1)
    ).subscribe(ratio => {
      const bvo = parseFloat((this.form.controls.fno.value * ratio).toFixed(2));
      this.buildingService.setRatio(this.building, ratio);
      this.action.emit({ ratio: ratio, fno: this.form.controls.fno.value, bvo: bvo });
    });
  }

  /**
   * Update form.
   *
   * The form is updated *after* the building is by patched by the API and approved by
   * the Project service.
   */
  private _updateForm(): void {
    const fno = this.buildingService.getFno(this.building);
    this.form.patchValue({
      ratio: parseFloat((this.building.ratio).toFixed(2)),
      fno: parseFloat(fno.toFixed(2)),
      bvo: parseFloat((fno * this.building.ratio).toFixed(2)),
      bui: this._getBuildingBui()
    }, {
      emitEvent: false
    });

    this._setDisabled();
  }
}
